import React from 'react';
import { Box } from '@chakra-ui/react';
import { sentenceCase } from 'change-case';
import { v4 as uuidv4 } from 'uuid';

import Text from './Text';
import DetailRow from './DetailRow';
import Accordion from './Accordion';
import { convertTokenValue, removeNulls } from '../utils/core';

const chainNativeTokenSymbol = (id) => ({
  mainnet: 'Ether',
  rinkeby: 'Ether',
  'palm-test': 'Palm',
  palm: 'Palm',
}[id]);

const parseTokenUri = tokenUri => {
  if (!`${tokenUri}`.includes('ipfs://')) {
    return tokenUri;
  }
  const [, path] = tokenUri.split('ipfs://');
  return `https://ipfs.io/ipfs/${path}`;
};

// @TODO - clean this up
const mapEventToDetails = ({
  token: {
    address: tokenAddress,
    tokenId,
    tokenUri,
  } = {},
  chain,
  eventName,
  feeData: {
    gasLimit,
    gasPrice,
    gasUsed = 0,
    cumulativeGasUsed,
    effectiveGasPrice,
    maxPriorityFeePerGas,
    baseFeePerGas,
    maxFeePerGas = (maxPriorityFeePerGas && baseFeePerGas)
      ? parseInt(maxPriorityFeePerGas, 10) + parseInt(baseFeePerGas, 10) : undefined,
  } = {},
  token: { symbol, units, value } = {},
  decimals: decimal,
  // Compued properties (doesnt belong on event)
  unit = chainNativeTokenSymbol(chain),
  denominator = unit === 'Gwei' ? 9 : 9,
  transactionFee = parseInt(gasUsed, 10) * (maxFeePerGas || parseInt(gasPrice, 10)),
  originChain,
  destinationChain,
  ...event
}) => removeNulls({
  label: sentenceCase(eventName),
  chain,
  ...event,
  tokenAddress,
  tokenId,
  tokenUri: parseTokenUri(tokenUri),
  units,
  symbol,
  value,
  decimal,
  // @TODO - incorporate use of decimal instead of hardcoding to 18
  ...((value && parseInt(value, 10) !== 0) && { amount: `${convertTokenValue(value, decimal)} ${symbol}` }),
  gasLimit: `${Number(gasLimit).toLocaleString()}`,
  gasUsed: `${Number(gasUsed).toLocaleString()} (${(gasUsed / gasLimit * 100).toFixed(2).toString()}%)`,
  cumulativeGasUsed: `${Number(cumulativeGasUsed).toLocaleString()}`,
  ...(effectiveGasPrice && {
    // eslint-disable-next-line max-len
    effectiveGasPrice: `${convertTokenValue(effectiveGasPrice, 18)} ${unit} (${convertTokenValue(effectiveGasPrice, denominator)} Gwei)`,
  }),
  // Should be null for EIP-1559
  ...(gasPrice && {
    gasPrice: `${convertTokenValue(gasPrice, 18)} ${unit} (${convertTokenValue(gasPrice, denominator)} Gwei)`,
  }),
  // Should be defined for EIP-1559 and not present for legacy chains
  ...(baseFeePerGas && {
    // eslint-disable-next-line max-len
    baseFeePerGas: `${convertTokenValue(baseFeePerGas, 18)} ${unit} (${convertTokenValue(baseFeePerGas, denominator)} Gwei)`,
  }),
  ...(maxPriorityFeePerGas && {
    // eslint-disable-next-line max-len
    maxPriorityFeePerGas: `${convertTokenValue(maxPriorityFeePerGas, 18)} ${unit} (${convertTokenValue(maxPriorityFeePerGas, denominator)} Gwei)`,
  }),
  ...(maxFeePerGas && {
    // eslint-disable-next-line max-len
    maxFeePerGas: `${convertTokenValue(maxFeePerGas, 18)} ${unit} (${convertTokenValue(maxFeePerGas, denominator)} Gwei)`,
  }),
  transactionFee: `${convertTokenValue(transactionFee, 18)} ${unit}`,
});

const TransactionsDetails = ({ transfer = {} }) => {
  const { events = [] } = transfer;
  const transactionsDetails = events.map(mapEventToDetails);
  return (
    <Box py="16px">
      <Text mb="16px" textStyle="h4" as="h4">Transactions Details</Text>
      <Box>
        {transactionsDetails.map(({ label, ...transactionDetails }, index) => (
          <Accordion
            key={uuidv4()}
            title={label}
            titleTextStyle="body"
            buttonStyle={{ fontSize: '16px' }}
            // pop open the last one so that users can still see that other transactions are available to expand
            defaultIndex={index === transactionsDetails.length - 1 ? [0] : undefined}
          >
            {Object.entries(transactionDetails).map(([label, value]) => (
              <DetailRow
                key={uuidv4()}
                mb="16px"
                label={sentenceCase(label)}
                value={value}
                valueProps={{ flexShrink: 'auto', wordBreak: 'break-word' }}
                labelProps={{ width: '35%' }}
              />
            ))}
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default TransactionsDetails;
